/**
 * The insurance estimate as the adjuster reads it.
 *
 * Xactimate's own layout, deliberately: an adjuster comparing our sheet to the
 * carrier's should be able to run a finger down both. Same seven columns in the
 * same order, the same continuous line numbering, the same per-area totals row,
 * and the same summary block ending in Net Claim.
 *
 * ## The branding is ours, and only ours
 *
 * Both reference estimates were printed under "The Roofing Dudes" with that
 * company's address. Jonathon, 2026-08-24: "it needs to have our logo and
 * contact details. just lysa's contact details. nothing for the roofing dudes."
 *
 * So the masthead carries the CopperRidge mark, the company's own long name and
 * address, and exactly one person to call. Three rules hold it there:
 *
 *  1. **The name is data.** It comes from `company_settings` via `branding`,
 *     never from a literal in this file — CLAUDE.md rule 11, and the name has
 *     already changed twice.
 *  2. **One contact, named in full.** Not a department, not a bare first name.
 *     A document an adjuster picks up should have one person on it to ring.
 *  3. **No licence number, ever.** The licence on file names a different
 *     company; the contract leaves it off for the same reason and so does this.
 *     There is no field for it here on purpose.
 *
 * Rendering is a pure function returning a string so that what leaves the
 * building can be asserted on in a test, exactly as `renderOrderHtml` does for
 * a work order. The browser's own "Save as PDF" makes the file; no PDF library
 * is involved and what is on screen is what comes out.
 */

import { esc } from "./stormReportHtml.js";
import { formatCents, type Cents } from "./money.js";
import type {
  InsuranceAreaTotals,
  InsuranceEstimateTotals,
  InsuranceLineTotals,
} from "./insuranceEstimate.js";

/** Who the document is from. Every field comes from `company_settings`. */
export interface InsuranceEstimateBranding {
  /** The company's trading name, long form. From `company_settings.name`. */
  companyName: string;
  /** The office address. Omitted from the page when blank. */
  companyAddress?: string | null;
  /** Absolute URL of the company mark. Absolute because the print window is
   *  written into blank and a relative path has no base to resolve against. */
  logoUrl?: string | null;
  /** The one person on the document. Full name — never "Lysa". */
  contactName: string;
  contactPhone?: string | null;
  contactEmail?: string | null;
}

/** What the estimate is about. All optional: a draft prints with gaps. */
export interface InsuranceEstimateHeading {
  /** The insured's name, as the carrier has it. */
  insuredName?: string | null;
  propertyAddress?: string | null;
  claimNumber?: string | null;
  carrier?: string | null;
  /** Date of loss and estimate date, already formatted for a reader. */
  dateOfLoss?: string | null;
  estimateDate?: string | null;
  /** The estimate's own name, used as the document title. */
  title?: string | null;
}

const money = (value: Cents): string => formatCents(value);

/** A quantity as the page shows it: two decimals, thousands separated. */
const qtyLabel = (qty: number, unit: string): string => {
  const number = qty.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return unit ? `${number} ${unit}` : number;
};

const headingRows = (heading: InsuranceEstimateHeading): string =>
  (
    [
      ["Insured", heading.insuredName],
      ["Property", heading.propertyAddress],
      ["Carrier", heading.carrier],
      ["Claim number", heading.claimNumber],
      ["Date of loss", heading.dateOfLoss],
      ["Estimate date", heading.estimateDate],
    ] as const
  )
    .filter(
      ([, value]) => value !== null && value !== undefined && value !== "",
    )
    .map(
      ([label, value]) =>
        `<tr><th>${esc(label)}</th><td>${esc(value)}</td></tr>`,
    )
    .join("");

const lineRow = (item: InsuranceLineTotals): string => `
  <tr class="item">
    <td class="desc"><span class="num">${item.number}.</span> ${esc(item.description)}</td>
    <td class="q">${esc(qtyLabel(item.qty, item.unit))}</td>
    <td class="n">${money(item.removePrice)}</td>
    <td class="n">${money(item.replacePrice)}</td>
    <td class="n">${money(item.tax)}</td>
    <td class="n">${money(item.overheadAndProfit)}</td>
    <td class="n tot">${money(item.total)}</td>
  </tr>
  ${
    item.note
      ? `<tr class="note"><td colspan="7">${esc(item.note)}</td></tr>`
      : ""
  }`;

const areaBlock = (area: InsuranceAreaTotals): string => `
  <section class="area">
    <h2>${esc(area.name)}</h2>
    <table class="items">
      <thead>
        <tr>
          <th class="desc">DESCRIPTION</th>
          <th class="q">QTY</th>
          <th class="n">REMOVE</th>
          <th class="n">REPLACE</th>
          <th class="n">TAX</th>
          <th class="n">O&amp;P</th>
          <th class="n">TOTAL</th>
        </tr>
      </thead>
      <tbody>
        ${
          area.items.length
            ? area.items.map(lineRow).join("")
            : `<tr><td colspan="7" class="empty">No line items in this area.</td></tr>`
        }
      </tbody>
      <tfoot>
        <tr>
          <td colspan="4">Totals: ${esc(area.name)}</td>
          <td class="n">${money(area.tax)}</td>
          <td class="n">${money(area.overheadAndProfit)}</td>
          <td class="n tot">${money(area.total)}</td>
        </tr>
      </tfoot>
    </table>
  </section>`;

/** The printed document, as HTML. Pure — nothing here reads a browser. */
export function renderInsuranceEstimateHtml(
  totals: InsuranceEstimateTotals,
  branding: InsuranceEstimateBranding,
  heading: InsuranceEstimateHeading = {},
): string {
  const title = heading.title?.trim() || "Insurance Estimate";

  // One line, built from whatever is filled in. A blank phone must not leave a
  // stranded separator on a page an adjuster reads.
  const contactLine = [
    branding.contactName,
    branding.contactPhone,
    branding.contactEmail,
  ]
    .filter((part) => part !== null && part !== undefined && part !== "")
    .map((part) => esc(part))
    .join(" &middot; ");

  const rows = headingRows(heading);

  return `<!doctype html><html><head>
<meta charset="utf-8">
<title>${esc(title)} — ${esc(branding.companyName)}</title>
<style>
  *{box-sizing:border-box}
  /* A printed page, not a screen. Paper is white and ink is dark whatever the
     browser's theme is: without this the preview renders navy-on-navy for
     anybody browsing in dark mode, and it is read on screen before it is
     printed. */
  :root{color-scheme:light}
  html{background:#fff}
  body{font:12px/1.45 system-ui,-apple-system,"Segoe UI",sans-serif;color:#16233d;background:#fff;margin:28px}
  .masthead{display:flex;align-items:center;gap:16px;border-bottom:2px solid #16233d;padding-bottom:14px;margin-bottom:18px}
  .masthead img{width:74px;height:74px;object-fit:contain;flex:none}
  .masthead .co{font-size:16px;font-weight:700;letter-spacing:.2px}
  .masthead .meta{font-size:11.5px;color:#4a5468;margin-top:3px}
  .masthead .contact{font-size:11.5px;color:#16233d;margin-top:4px;font-weight:600}
  h1{font-size:17px;margin:0 0 12px}
  h2{font-size:12.5px;margin:20px 0 6px;text-transform:uppercase;letter-spacing:.6px;color:#16233d}
  table{border-collapse:collapse;width:100%}
  table.heading{width:auto;min-width:52%;margin-bottom:6px}
  table.heading th{text-align:left;color:#6b7688;font-weight:600;padding:3px 14px 3px 0;white-space:nowrap}
  table.heading td{padding:3px 0}
  table.items th{background:#f2f5f9;font-size:10.5px;letter-spacing:.4px;color:#4a5468;
    border-top:1px solid #cdd5e0;border-bottom:1px solid #cdd5e0;padding:5px 6px;text-align:left}
  table.items td{padding:5px 6px;border-bottom:1px solid #eceff4;vertical-align:top}
  td.desc,th.desc{width:40%}
  td.q,th.q{white-space:nowrap;text-align:right;width:11%}
  .n{text-align:right;white-space:nowrap}
  .tot{font-weight:600}
  .num{color:#6b7688}
  tr.note td{border-bottom:1px solid #eceff4;padding:0 6px 8px 6px;color:#4a5468;font-size:10.5px;line-height:1.45}
  tr.item td{border-bottom:0}
  tfoot td{font-weight:700;border-top:1.5px solid #16233d;border-bottom:0;padding:6px}
  .empty{color:#6b7688;font-style:italic}
  .summary{margin-top:26px;page-break-inside:avoid}
  table.sum{width:auto;min-width:320px;margin-left:auto}
  table.sum td{padding:5px 8px;border-bottom:1px solid #eceff4}
  table.sum td.n{text-align:right;white-space:nowrap}
  table.sum tr.rule td{border-top:1.5px solid #16233d;font-weight:700}
  table.sum tr.claim td{border-top:2px solid #16233d;border-bottom:0;font-size:14px;font-weight:700}
  @media print{body{margin:0.4in}h2{page-break-after:avoid}table.items{page-break-inside:auto}tr{page-break-inside:avoid}}
</style></head><body>

<div class="masthead">
  ${
    branding.logoUrl
      ? `<img src="${esc(branding.logoUrl)}" alt="${esc(branding.companyName)}">`
      : ""
  }
  <div>
    <div class="co">${esc(branding.companyName)}</div>
    ${branding.companyAddress ? `<div class="meta">${esc(branding.companyAddress)}</div>` : ""}
    ${contactLine ? `<div class="contact">${contactLine}</div>` : ""}
  </div>
</div>

<h1>${esc(title)}</h1>
${rows ? `<table class="heading">${rows}</table>` : ""}

${totals.areas.map(areaBlock).join("")}

<section class="summary">
  <h2>Summary</h2>
  <table class="sum">
    <tr><td>Line Item Total</td><td class="n">$${money(totals.lineItemTotal)}</td></tr>
    <tr><td>Material Sales Tax</td><td class="n">$${money(totals.materialSalesTax)}</td></tr>
    <tr class="rule"><td>Subtotal</td><td class="n">$${money(totals.subtotal)}</td></tr>
    <tr><td>Overhead</td><td class="n">$${money(totals.overhead)}</td></tr>
    <tr><td>Profit</td><td class="n">$${money(totals.profit)}</td></tr>
    <tr class="rule"><td>Replacement Cost Value</td><td class="n">$${money(totals.replacementCostValue)}</td></tr>
    <tr class="claim"><td>Net Claim</td><td class="n">$${money(totals.netClaim)}</td></tr>
  </table>
</section>

</body></html>`;
}
